
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tenant from '../../config/tenant';
import { getAuthKeyFromLocalStorage } from './employeeSlice';



function authHeaders(token) {
  return { 'Content-Type': 'application/json', 'auth-key': token };
}

export const fetchLocationMaster = createAsyncThunk(
  'locationMaster/fetchAll',
  async (_, { rejectWithValue }) => {
    const token = getAuthKeyFromLocalStorage();
    if (!token) return rejectWithValue('Authentication required. Please login again.');

    try {
      const res = await fetch(`${tenant.api.baseUrl}/EMP/GetLocationDetails`, {
        method: 'GET',
        headers: authHeaders(token),
      });

      
      if (res.status === 404) return { rows: [] };

      if (!res.ok) {
        const text = await res.text().catch(() => `HTTP ${res.status}`);
        return rejectWithValue(text || `HTTP error ${res.status}`);
      }

      const data = await res.json().catch(() => ({}));

      
      
      
      if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
        if (data.StatusCode === 404 || /no\s*(data|record)/i.test(data.Message || '')) {
          return { rows: [] };
        }
        return rejectWithValue(data.Message || `Server error: status ${data.StatusCode}`);
      }

      const resultSet = Array.isArray(data) ? data : (data ? data.ResultSet : null);
      const rows = Array.isArray(resultSet) ? resultSet : (resultSet ? [resultSet] : []);
      return { rows };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

export const addLocationMaster = createAsyncThunk(
  'locationMaster/add',
  async ({ code, description, latitude, longitude }, { rejectWithValue }) => {
    if (!code)        return rejectWithValue('Location code is required.');
    if (!description) return rejectWithValue('Description is required.');

    const token = getAuthKeyFromLocalStorage();
    if (!token) return rejectWithValue('Authentication required. Please login again.');

    try {
      const res = await fetch(`${tenant.api.baseUrl}/EMP/AddLocation`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          p_loc_code:   code,
          p_loc_desc:   description,
          p_longitute:  longitude != null ? String(longitude) : '',
          p_latitude:   latitude  != null ? String(latitude)  : '',
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => `HTTP ${res.status}`);
        return rejectWithValue(text || `HTTP error ${res.status}`);
      }

      const data = await res.json().catch(() => ({}));
      if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
        return rejectWithValue(data.Message || `Server error: status ${data.StatusCode}`);
      }

      return { code };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

const initialState = {
  rows: [],
  loading: false,
  loaded: false,
  error: null,
  adding: false,
  addError: null,
  addSuccess: false,
};

const locationMasterSlice = createSlice({
  name: 'locationMaster',
  initialState,
  reducers: {
    clearLocationMasterAddState(state) {
      state.adding = false;
      state.addError = null;
      state.addSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLocationMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocationMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.rows = action.payload.rows;
      })
      .addCase(fetchLocationMaster.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload ?? 'Failed to load physical locations';
      })
      .addCase(addLocationMaster.pending, (state) => {
        state.adding = true;
        state.addError = null;
        state.addSuccess = false;
      })
      .addCase(addLocationMaster.fulfilled, (state) => {
        state.adding = false;
        state.addSuccess = true;
      })
      .addCase(addLocationMaster.rejected, (state, action) => {
        state.adding = false;
        state.addError = action.payload ?? 'Failed to add physical location';
      });
  },
});

export const { clearLocationMasterAddState } = locationMasterSlice.actions;
export default locationMasterSlice.reducer;
